"use client"

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from '../../../components/layout/ThemeProvider';
import ContactForm from '../../../components/ui/ContactForm';
import ServiceButton from '../../../components/ui/ServiceButton';

const DesenvolvimentoSistemasPage = () => {
  const { theme } = useTheme();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 } 
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  // Etapas do processo de desenvolvimento
  const processoDesenvolvimento = [
    {
      step: 1,
      title: 'Entendimento do Negócio',
      description: 'Imersão completa nos processos e desafios da sua empresa',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      step: 2,
      title: 'Mapeamento de Processos',
      description: 'Documentação detalhada dos fluxos de trabalho atuais',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      step: 3,
      title: 'Arquitetura do Sistema',
      description: 'Definição da estrutura técnica e de dados ideal',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },
    {
      step: 4,
      title: 'Prototipagem & UX Design',
      description: 'Interfaces intuitivas com foco na experiência do usuário',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      )
    },
    {
      step: 5,
      title: 'Desenvolvimento Seguro',
      description: 'Codificação com padrões modernos e foco em segurança',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      )
    },
    {
      step: 6,
      title: 'Testes & Refinamento',
      description: 'Validação exaustiva de funcionalidades e segurança',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      step: 7,
      title: 'Implantação & Treinamento',
      description: 'Transição suave e capacitação completa da sua equipe',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      )
    }
  ];

  // Comparativo entre sistemas personalizados e soluções prontas
  const comparativoTabela = [
    {
      criterio: 'Alinhamento com o negócio',
      personalizado: 'Sistema desenvolvido exatamente para sua forma de trabalhar',
      pronto: 'Processos da empresa precisam se adaptar ao software'
    },
    {
      criterio: 'Custo-benefício',
      personalizado: 'Valor investido em funcionalidades que realmente usará',
      pronto: 'Pagamento por recursos que muitas vezes não são necessários'
    },
    {
      criterio: 'Propriedade e autonomia',
      personalizado: 'O código é 100% seu, sem dependência do fornecedor',
      pronto: 'Dependência permanente do fornecedor para qualquer alteração'
    },
    {
      criterio: 'Escalabilidade',
      personalizado: 'Crescimento planejado conforme evolução do negócio',
      pronto: 'Limitações técnicas para expansões específicas'
    },
    {
      criterio: 'Segurança',
      personalizado: 'Controles e proteções específicos para seu modelo de negócio',
      pronto: 'Vulnerabilidades conhecidas por ser software de massa'
    },
    {
      criterio: 'Integração com outros sistemas',
      personalizado: 'Flexibilidade total para conectar com qualquer plataforma',
      pronto: 'Integrações limitadas ou custosas de implementar'
    }
  ];

  // Tecnologias utilizadas
  const tecnologias = [
    {
      categoria: 'Frontend',
      techs: ['React', 'Angular', 'Vue.js', 'Next.js', 'Bootstrap'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      categoria: 'Backend',
      techs: ['Node.js', '.NET Core', 'Java Spring', 'Laravel', 'Python Django'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
        </svg>
      )
    },
    {
      categoria: 'Bancos de Dados',
      techs: ['PostgreSQL', 'MySQL', 'MongoDB', 'SQL Server', 'Redis'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      )
    },
    {
      categoria: 'DevOps',
      techs: ['Docker', 'CI/CD', 'AWS', 'Azure', 'Kubernetes'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )
    },
    {
      categoria: 'Segurança',
      techs: ['OAuth 2.0', 'JWT', 'Criptografia', 'Firewall', 'Penetration Testing'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      categoria: 'Ferramentas de Gestão',
      techs: ['Jira', 'GitHub', 'Confluence', 'Trello', 'Azure DevOps'],
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    }
  ];

  // Cases de sucesso
  const casesSucesso = [
    {
      nome: 'Sistema de Gestão para Indústria',
      desafio: 'Substituir 23 planilhas Excel e 3 sistemas desconectados por uma solução integrada de controle de produção, estoque e vendas.',
      solucao: 'Desenvolvemos um ERP personalizado com módulos específicos para o modelo de negócio, centralizando todas as operações.',
      resultados: 'Redução de 72% no tempo de processamento de pedidos e eliminação de inconsistências de dados entre departamentos.',
      imagem: '/images/cases/sistema-gestao-case.jpg'
    },
    {
      nome: 'Dashboard para Setor Financeiro',
      desafio: 'Criar visualização em tempo real dos principais indicadores financeiros de uma empresa com múltiplas filiais.',
      solucao: 'Sistema com integração APIs bancárias e contábeis, consolidando dados em painéis personalizados por nível gerencial.',
      resultados: 'Tomada de decisão 3x mais rápida e redução de 45% nas reuniões de alinhamento entre departamentos.',
      imagem: '/images/cases/dashboard-financeiro-case.jpg'
    },
    {
      nome: 'Automação de Processos Comerciais',
      desafio: 'Equipe comercial perdia tempo com tarefas manuais de cadastro, propostas e acompanhamento de leads.',
      solucao: 'Sistema com fluxo automatizado desde a captura do lead até pós-venda, incluindo geração inteligente de propostas.',
      resultados: 'Aumento de 38% na conversão de vendas e capacidade de atendimento duplicada com o mesmo número de vendedores.',
      imagem: '/images/cases/automacao-comercial-case.jpg'
    }
  ];

  // FAQ items
  const faqItems = [
    {
      pergunta: 'Quanto tempo leva para desenvolver um sistema personalizado?',
      resposta: 'O prazo varia conforme a complexidade do projeto. Sistemas mais simples podem levar de 2 a 3 meses, enquanto projetos mais complexos podem demandar 6 meses ou mais. Trabalhamos com entregas incrementais, permitindo que você comece a utilizar módulos específicos antes da conclusão total.'
    },
    {
      pergunta: 'O sistema realmente será 100% meu depois de pronto?',
      resposta: 'Sim, absolutamente. Entregamos toda a documentação, código-fonte e direitos de propriedade intelectual. Você não fica dependente de nós para manutenções ou atualizações futuras, embora ofereçamos estes serviços caso deseje continuar com nosso suporte especializado.'
    },
    {
      pergunta: 'Como é feita a transição das planilhas atuais para o novo sistema?',
      resposta: 'Implementamos um processo gradual que inclui: análise detalhada das planilhas e processos atuais, migração de dados para o novo sistema, período de uso paralelo para validação, treinamento personalizado para sua equipe e acompanhamento contínuo até que todos estejam confortáveis com a nova solução.'
    },
    {
      pergunta: 'O sistema pode ser acessado de qualquer lugar?',
      resposta: 'Sim, desenvolvemos sistemas web que podem ser acessados de qualquer dispositivo com internet, implementando níveis adequados de segurança para acesso remoto. Também podemos hospedar o sistema em um servidor dentro da empresa do cliente e manter o acesso apenas interno caso o cliente opte por isso.'
    },
    {
      pergunta: 'É possível integrar o sistema com outros softwares que já utilizamos?',
      resposta: 'Absolutamente. Nossos sistemas são desenvolvidos considerando a integração com outras plataformas, seja através de APIs, webhooks ou outros métodos de comunicação. Podemos conectar seu sistema com ERPs, CRMs, plataformas de e-commerce, sistemas bancários, entre outros.'
    },
    {
      pergunta: 'Oferecemos suporte e manutenção após a entrega do sistema?',
      resposta: 'Sim, temos planos flexíveis de suporte pós-entrega que incluem correções, melhorias, atualizações de segurança e implementação de novas funcionalidades. No entanto, como o sistema é de sua propriedade, você tem total liberdade para gerenciar esse suporte internamente ou com outros fornecedores, se preferir.'
    }
  ];

  // Schema.org JSON-LD
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Sistemas Empresariais Personalizados",
    "description": "Transformamos seus processos empresariais em sistemas digitais poderosos, seguros e intuitivos, desenvolvidos especificamente para o seu negócio.",
    "image": "/images/desenvolvimento-sistemas-illustration.svg",
    "brand": {
      "@type": "Brand",
      "name": "WebImports"
    },
    "offers": {
      "@type": "Offer",
      "price": "R$ 284.560",
      "priceCurrency": "BRL",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "WebImports"
      }
    }
  };

  return (
    <>
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <Link 
                  href="/servicos" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Serviços
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Desenvolvimento de Sistemas
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#3B82F6" : "#60A5FA"} 
              fillOpacity="0.2" 
            />
          </svg>
          
          <svg 
            className="absolute left-0 bottom-0 transform -translate-x-1/4 translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.2" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={fadeIn}
              className="lg:w-1/2"
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Sistemas Empresariais Personalizados
              </div>
              <h1 className={`text-3xl lg:text-4xl font-bold leading-tight ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Transforme sua Empresa com Tecnologia <span className="text-blue-600">Sob Medida</span>
              </h1>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Transformamos seus processos empresariais em sistemas digitais poderosos,
                seguros e intuitivos, desenvolvidos especificamente para o seu negócio.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <ServiceButton 
                  text="Solicitar Orçamento"
                  href="#contato"
                  className="text-white font-bold"
                />
                <ServiceButton 
                  text="Conhecer Mais"
                  href="#transicao"
                  isPrimary={false}
                  className="font-bold"
                />
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <div className={`w-full h-80 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'
                } flex items-center justify-center overflow-hidden`}>
                  <Image
                    src="/images/desenvolvimento-sistemas-illustration.svg"
                    alt="Sistemas Empresariais Personalizados"
                    width={500}
                    height={400}
                    className="object-contain max-w-full max-h-full"
                  />
                </div>
                
                {/* Estatística flutuante */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className={`absolute -bottom-6 -right-6 p-6 rounded-lg shadow-xl ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                  }`}
                >
                  <p className="text-blue-600 font-bold text-4xl">+65%</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    aumento de eficiência operacional
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Processo de Desenvolvimento Section */}
      <section 
        id="processo" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Metodologia
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Processo de Desenvolvimento Estruturado
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Um processo estruturado que garante resultados alinhados às necessidades do seu negócio
            </p>
          </div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {processoDesenvolvimento.map((passo) => (
              <motion.div
                key={passo.step}
                variants={fadeIn}
                className={`relative ${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-4 shadow-lg`}
              >
                <div className={`flex items-center mb-3`}>
                  <div className={`w-8 h-8 rounded-lg ${
                    theme === 'dark' 
                      ? 'bg-gradient-to-br from-blue-900 to-blue-800 text-blue-300' 
                      : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                    } flex items-center justify-center mr-2 font-medium`}>
                    {passo.step}
                  </div>
                  <h3 className={`text-lg font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>{passo.title}</h3>
                </div>
                <p className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } pl-10`}>
                  {passo.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Transição Digital Section */}
      <section 
        id="transicao"
        className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Transformação Digital
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}>
              Da Planilha ao Sistema Integrado
            </h2>
            <p className={`text-xl ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Convertemos suas planilhas e processos manuais em sistemas poderosos que impulsionam seu negócio
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className={`p-6 rounded-xl ${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${
                    theme === 'dark' ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
                  } flex-shrink-0 mt-1`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold mb-2 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Problemas com Planilhas</h3>
                    <ul className={`space-y-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span>Inconsistência de informações entre múltiplas planilhas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span>Tempo excessivo para gerar relatórios consolidados</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span>Risco constante de erros humanos e perda de dados</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className={`p-6 rounded-xl ${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${
                    theme === 'dark' ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                  } flex-shrink-0 mt-1`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold mb-2 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Benefícios do Sistema</h3>
                    <ul className={`space-y-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Dados centralizados e consistentes em tempo real</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Automação de cálculos e relatórios com um clique</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Controle de acesso e segurança em múltiplos níveis</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className={`p-6 rounded-xl ${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-lg'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                  } flex-shrink-0 mt-1`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold mb-2 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Resultados Comprovados</h3>
                    <ul className={`space-y-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Redução média de 65% no tempo de processamento</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Diminuição de 95% nos erros operacionais</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Tomada de decisão baseada em dados em tempo real</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className={`w-full rounded-lg overflow-hidden ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-xl'
              }`}>
                <div className="flex justify-between items-center px-4 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Sistema ERP</div>
                </div>
                
                <div className="p-4">
                  <div className="flex gap-4 mb-4">
                    <div className={`w-1/4 h-full p-3 ${
                      theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
                    } rounded-lg`}>
                      <div className="space-y-2">
                        <div className={`w-full h-8 ${
                          theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                        } rounded flex items-center justify-center`}>
                          <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                          }`}>Dashboard</span>
                        </div>
                        <div className={`w-full h-8 ${
                          theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                        } rounded`}></div>
                        <div className={`w-full h-8 ${
                          theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                        } rounded`}></div>
                        <div className={`w-full h-8 ${
                          theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                        } rounded`}></div>
                      </div>
                    </div>
                    
                    <div className="w-3/4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className={`p-4 rounded-lg ${
                          theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'
                        }`}>
                          <h4 className={`text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                          }`}>Faturamento</h4>
                          <p className={`text-2xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>R$ 284.560</p>
                          <p className={`text-xs ${
                            theme === 'dark' ? 'text-green-300' : 'text-green-600'
                          } flex items-center`}>
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M12 7a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L12 12.586V7z" clipRule="evenodd" />
                            </svg>
                            +12,5% este mês
                          </p>
                        </div>
                        
                        <div className={`p-4 rounded-lg ${
                          theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50'
                        }`}>
                          <h4 className={`text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-green-300' : 'text-green-800'
                          }`}>Produtividade</h4>
                          <p className={`text-2xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>89%</p>
                          <p className={`text-xs ${
                            theme === 'dark' ? 'text-green-300' : 'text-green-600'
                          } flex items-center`}>
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M12 7a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L12 12.586V7z" clipRule="evenodd" />
                            </svg>
                            +8,2% vs. trimestre anterior
                          </p>
                        </div>
                      </div>
                      
                      <div className={`p-4 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-900' : 'bg-white border border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>Evolução Anual</h4>
                          <div className={`text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>2023 - 2024</div>
                        </div>
                        
                        <div className="h-24 flex items-end gap-2">
                          {[40, 55, 45, 65, 70, 85, 75, 90, 95, 100, 85, 110].map((height, i) => (
                            <div 
                              key={i} 
                              className="flex-1 rounded-t relative group"
                              style={{height: `${height * 0.2}px`}}
                            >
                              <div className={`w-full h-full ${
                                theme === 'dark' 
                                  ? 'bg-gradient-to-t from-blue-900/50 to-indigo-900/50' 
                                  : 'bg-gradient-to-t from-blue-500/80 to-indigo-500/80'
                              } rounded-t`}></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-900' : 'bg-white border border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>Últimas Transações</h4>
                      <button className={`text-xs ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      }`}>Ver todas</button>
                    </div>
                    
                    <div className="space-y-2">
                      {[1, 2, 3].map((item) => (
                        <div 
                          key={item}
                          className={`flex items-center justify-between py-2 ${
                            item < 3 ? theme === 'dark' ? 'border-b border-gray-800' : 'border-b border-gray-100' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
                            }`}>
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>Pedido #{10548 + item}</p>
                              <p className={`text-xs ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>Cliente {item === 1 ? 'Premium' : item === 2 ? 'Corporate' : 'Standard'}</p>
                            </div>
                          </div>
                          <div className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>R$ {(Math.floor(Math.random() * 50) + 10) * 100}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -left-6 transform rotate-12">
                <div className={`w-32 h-40 ${
                  theme === 'dark' ? 'bg-gray-800 shadow-lg border border-gray-700' : 'bg-white shadow-xl'
                } rounded-lg p-3 flex flex-col`}>
                  <div className="flex justify-between items-center mb-1">
                    <div className="w-12 h-2 bg-green-500 rounded"></div>
                    <div className="w-4 h-4 rounded-full border-2 border-green-500"></div>
                  </div>
                  <div className="flex-grow grid grid-cols-4 grid-rows-5 gap-1">
                    {Array(20).fill(0).map((_, i) => (
                      <div 
                        key={i}
                        className={`${
                          theme === 'dark' 
                            ? i % 3 === 0 ? 'bg-gray-700' : 'bg-gray-900' 
                            : i % 3 === 0 ? 'bg-gray-200' : 'bg-gray-100'
                        } rounded-sm`}
                      ></div>
                    ))}
                  </div>
                  <div className="mt-2 h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparativo Section */}
      <section 
        id="diferencial" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Vantagens
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Entenda as vantagens de investir em um sistema feito sob medida para o seu negócio
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Entregamos sistemas personalizados que são projetados para atender às suas necessidades específicas, 
              proporcionando uma experiência de usuário superior e uma integração perfeita com outros sistemas.
            </p>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className={`overflow-hidden rounded-xl shadow-xl border ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`${
                    theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
                  }`}>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Critério</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${
                      theme === 'dark' ? 'bg-blue-900/50 text-blue-100' : 'bg-blue-50 text-blue-900'
                    }`}>Sistema Personalizado</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${
                      theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>Software Pronto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {comparativoTabela.map((item, index) => (
                    <tr key={index} className={`${
                      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    } ${index % 2 === 1 ? theme === 'dark' ? 'bg-gray-800/70' : 'bg-gray-50' : ''}`}>
                      <td className={`px-6 py-4 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                      } font-medium`}>{item.criterio}</td>
                      <td className={`px-6 py-4 ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                      }`}>
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>{item.personalizado}</span>
                        </div>
                      </td>
                      <td className={`px-6 py-4 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span>{item.pronto}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
          
          <div className="mt-10 text-center">
            <motion.a 
              href="#contato" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-extrabold text-lg tracking-wide ${
                theme === 'dark' 
                  ? 'bg-blue-700 hover:bg-blue-600 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              } shadow-lg hover:shadow-xl transition-all`}
              style={{
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                color: '#ffffff'
              }}
            >
              <span>Consultar sobre seu Projeto</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
          </div>
        </div>
      </section>

      {/* Tecnologias Section */}
      <section 
        id="tecnologias"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-6">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-2`}>
              Tecnologias
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Ferramentas Utilizadas em Nosso Processo
            </h2>
            <p className={`${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Soluções modernas e eficientes para cada necessidade específica
            </p>
          </div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 lg:grid-cols-6 gap-3"
          >
            {tecnologias.map((tech, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className={`${
                  theme === 'dark' ? 'bg-gray-900 border border-gray-700' : 'bg-white shadow'
                } rounded-xl p-3`}
              >                
                <h3 className={`text-sm font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}>{tech.categoria}</h3>
                
                <div className="flex flex-wrap gap-1 mt-2">
                  {tech.techs.map((item, i) => (
                    <span
                      key={i}
                      className={`px-2 py-1 rounded-full text-xs ${
                        theme === 'dark' 
                          ? 'bg-gray-800 text-blue-300 border border-blue-900' 
                          : 'bg-blue-50 text-blue-800'
                      }`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfólio Section */}
      <section 
        id="portfolio"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Cases de Sucesso
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Transformamos Negócios através da Tecnologia
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Conheça alguns dos sistemas que desenvolvemos e os resultados que nossos clientes alcançaram
            </p>
          </div>
          
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
            {casesSucesso.map((caso, index) => (
              <motion.div
                key={caso.nome}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-xl'
                } rounded-xl overflow-hidden h-full flex flex-col`}
              >
                <div className="h-40 overflow-hidden">
                  <Image 
                    src={caso.imagem} 
                    alt={caso.nome}
                    width={500}
                    height={300}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className={`text-xl font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  } mb-3`}>{caso.nome}</h3>
                  
                  <div className="space-y-3 mb-3 flex-1">
                    <div>
                      <h4 className={`text-sm font-semibold ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                      } mb-1`}>Desafio:</h4>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm`}>{caso.desafio}</p>
                    </div>
                    
                    <div>
                      <h4 className={`text-sm font-semibold ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                      } mb-1`}>Solução:</h4>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm`}>{caso.solucao}</p>
                    </div>
                    
                    <div>
                      <h4 className={`text-sm font-semibold ${
                        theme === 'dark' ? 'text-green-300' : 'text-green-700'
                      } mb-1`}>Resultados:</h4>
                      <p className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm font-medium`}>{caso.resultados}</p>
                    </div>
                  </div>
                  
                  <motion.a 
                    href={`/case/${caso.nome.toLowerCase().replace(/\s+/g, '-')}`}
                    whileHover={{ x: 5 }}
                    className={`inline-flex items-center text-sm font-medium ${
                      theme === 'dark' 
                        ? 'text-blue-400 hover:text-blue-300' 
                        : 'text-blue-700 hover:text-blue-800'
                    }`}
                  >
                    <span>Ver detalhes do projeto</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </motion.a>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <motion.a 
              href="/portfolio?categoria=desenvolvimento-sistemas" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-extrabold text-lg tracking-wide ${
                theme === 'dark' 
                  ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              } shadow-lg hover:shadow-xl transition-all`}
              style={{
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                color: '#ffffff'
              }}
            >
              <span>Ver Todos os Projetos</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Perguntas Frequentes
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Dúvidas comuns sobre nossos serviços de desenvolvimento de sistemas personalizados.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto"
               itemScope
               itemType="https://schema.org/FAQPage">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`mb-6 rounded-xl ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                } shadow-md overflow-hidden`}
                itemScope
                itemType="https://schema.org/Question"
              >
                <details className="group">
                  <summary className={`flex items-center justify-between p-6 cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <h3 className="text-lg font-semibold" itemProp="name">{item.pergunta}</h3>
                    <span className="relative ml-2 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transition-transform transform ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        } group-open:rotate-180`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </span>
                  </summary>
                  <div 
                    className={`px-6 pb-6 pt-0 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    itemScope
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                  >
                    <p itemProp="text">{item.resposta}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="contato"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#1E40AF" : "#3B82F6"} 
              fillOpacity="0.05" 
            />
          </svg>
          
          <svg 
            className="absolute bottom-0 right-0 transform translate-x-1/2 translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.05" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Vamos Conversar
              </div>
              <h2 className={`text-2xl md:text-3xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-3`}>
                Transforme seus Processos em Sistemas Eficientes
              </h2>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Entre em contato para uma consultoria gratuita. Entenderemos seus desafios e mostraremos 
                como podemos ajudar a otimizar seu negócio através da tecnologia.
              </p>
              
              <div className="space-y-4">
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a 
                      href="tel:+551932060706" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      (19) 3206-0706
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-green-900/40' : 'bg-green-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-green-300' : 'text-green-700'
                  }`}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a 
                      href="https://wa.me/5519994799400" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${
                        theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      (19) 99479-9400
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a 
                      href="mailto:contato@webimports.com.br" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      contato@webimports.com.br
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className={`${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-xl'
              } rounded-xl p-5`}
            >
              <h3 className={`text-xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-6`}>
                Solicite uma consultoria especializada
              </h3>
              
              <ContactForm />
              
              <p className={`mt-4 text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Ao enviar este formulário, você concorda com nossa política de privacidade.
                Seus dados não serão compartilhados com terceiros.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DesenvolvimentoSistemasPage;